import React, { useContext, useState } from 'react';
import NextLink from 'next/link';
import { Typography } from '@dapp/multi-lib/sdaoui/Typography';
import { cx } from 'class-variance-authority';
import { DesktopLayoutContext } from 'structure/Layouts/LayoutWithSideBar/desktop/DesktopLayoutContext';
import { Collapse } from '@dapp/multi-lib/sdaoui/Collapse';
import * as Menu from 'structure/Layouts/LayoutWithSideBar/menu';
import { Icon } from '@dapp/multi-lib/sdaoui/Icon';
import { twMerge } from 'tailwind-merge';

type MenuItemProps = Omit<Menu.MenuItem, 'items' | 'icon'> & {
  icon: React.ReactNode;
  children?: React.ReactNode;
};

export const MenuItem = (props: MenuItemProps) => {
  const { icon, label, children, href, target } = props;
  const [expandChild, setExpandChild] = useState(false);

  const desktopLayoutContext = useContext(DesktopLayoutContext);
  const hasChild = children && React.Children.count(children) > 0;

  const handleClickExpandChild = () => {
    if (hasChild) {
      setExpandChild((prev) => {
        let nextValue = !prev;

        const forceExpandSidebar =
          desktopLayoutContext.sideBarExpanded === false;
        if (forceExpandSidebar) {
          desktopLayoutContext.setSideBarExpanded(true);
          nextValue = true;
        }

        return nextValue;
      });
    }
  };

  const isLink = !hasChild && !!href;

  const wrapperClassName = twMerge(
    'flex cursor-pointer flex-row items-center gap-2 transition hover:scale-110 active:scale-90 active:opacity-70',
    !desktopLayoutContext.sideBarExpanded && 'gap-1'
  );

  const innerContent = (
    <>
      {icon}
      <Collapse
        visible={desktopLayoutContext.sideBarExpanded}
        type='horizontal'
      >
        <Typography className='whitespace-nowrap font-[Montserrat] text-sm font-normal text-gray-200'>
          {label}
        </Typography>
      </Collapse>
      {hasChild && (
        <div
          className={cx(
            'transition-all duration-300 ease-in-out',
            expandChild && desktopLayoutContext.sideBarExpanded
              ? 'rotate-0'
              : '-rotate-180'
          )}
        >
          <Icon icon='arrow_drop_up' width={24} height={24} color='#C1CAD6' />
        </div>
      )}
    </>
  );

  return (
    <div className='flex flex-col'>
      {isLink ? (
        <NextLink
          href={href as string}
          target={target}
          className={wrapperClassName}
          onClick={handleClickExpandChild}
        >
          {innerContent}
        </NextLink>
      ) : (
        <div className={wrapperClassName} onClick={handleClickExpandChild}>
          {innerContent}
        </div>
      )}
      {hasChild && (
        <Collapse
          className={cx(!desktopLayoutContext.sideBarExpanded && 'w-0')}
          visible={desktopLayoutContext.sideBarExpanded && expandChild}
          type='vertical'
        >
          <div className='-mt-2 pb-2'>
            {React.Children.map(children, (child) => child)}
          </div>
        </Collapse>
      )}
    </div>
  );
};
